import dayjs from 'dayjs'
import { GeckoInspection } from 'types'
import { DATE_FORMAT, INSPECTION_SLUG_DATE_FORMAT } from 'utils/fde/constants'

/**
 * Gets the inspection date from either the inspectionSlug or inspection date.
 * We take both parameters as options for two reasons:
 * 1. Some older inspections do not have the date field filled out, so we need to parse it from the slug.
 * 2. Some callers of this function do not have access to the inspection entry, only the slug from a tagged entry
 *
 * If the date cannot be parsed from either the inspection or the slug, this function will return null.
 * @param inspectionSlug The slug of the inspection, if available
 * @param inspection Parsed inspection entry, if available
 * @returns date or null
 */
export function getInspectionDate({
  inspectionSlug,
  inspection,
}: {
  inspectionSlug?: string
  inspection?: GeckoInspection
}): dayjs.Dayjs | null {
  if (inspection?.data.date) {
    const inspectionDate = dayjs(inspection.data.date)
    if (inspectionDate.isValid()) {
      return inspectionDate
    }
  }

  if (!inspectionSlug) {
    return null
  }

  const DEFAULT_INSPECTION_SLUG_FORMAT = /^\d{4}[01]\d[0-3]\d-[A-Za-z0-9]{6}$/

  // Some RAUT inspections have the date formatted this way in the slug
  const SECONDARY_INSPECTION_DATE_FORMAT = /\d{4}-\d{2}-\d{2}$/

  let inspectionDate: dayjs.Dayjs | null = null
  if (DEFAULT_INSPECTION_SLUG_FORMAT.test(inspectionSlug)) {
    const dateString = inspectionSlug.split('-')[0]
    inspectionDate = dayjs(dateString, INSPECTION_SLUG_DATE_FORMAT)
  } else {
    const dateMatches = inspectionSlug.match(SECONDARY_INSPECTION_DATE_FORMAT)
    if (!!dateMatches && dateMatches.length > 0) {
      inspectionDate = dayjs(dateMatches[0], DATE_FORMAT)
    }
  }

  return inspectionDate && inspectionDate.isValid() ? inspectionDate : null
}
